import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Empty, Form, Input, message, Modal, Popconfirm, Segmented, Select, Space, Spin, Switch, Table, Tag, Tooltip } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, InstagramOutlined, WarningOutlined, LinkOutlined } from '@ant-design/icons';
import { liveChatApi, show422Error, ValidationError } from '../../utils/api';
import { PANEL_PRIMARY_BTN_CLASS } from '../../constants/ui';
import InstagramPostPicker from './InstagramPostPicker';

/** حد متن دایرکت اینستاگرام؛ هر حرف فارسی ۲ بایت است */
const MAX_DIRECT_BYTES = 1000;
const byteLength = (text) => new TextEncoder().encode(text || '').length;

const TARGET_OPTIONS = [
  { value: 'all', label: <span className="custom-font">همه پست‌ها</span> },
  { value: 'post', label: <span className="custom-font">یک پست مشخص</span> },
];

/** پستی که قانون به آن محدود است */
const PostCell = ({ rule, accounts }) => {
  if (!rule.external_post_id) return <Tag className="custom-font">همه پست‌ها</Tag>;
  const account = accounts.length > 1 ? accounts.find((a) => a.id === rule.channel_id) : null;
  const title = rule.post_caption || 'پست بدون متن';
  return (
    <div className="custom-font text-xs leading-5 min-w-0">
      {rule.post_permalink ? (
        <a href={rule.post_permalink} target="_blank" rel="noopener noreferrer" className="inline-flex items-start gap-1 max-w-full">
          <LinkOutlined className="mt-1 shrink-0" />
          <span className="break-words" style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{title}</span>
        </a>
      ) : (
        <span className="break-words" style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{title}</span>
      )}
      {account && <div className="text-gray-400 mt-0.5 truncate">{account.username ? `@${account.username}` : account.name}</div>}
    </div>
  );
};

const MessageCell = ({ value }) =>
  value ? <div className="whitespace-pre-wrap break-words custom-font text-sm">{value}</div> : <span className="text-gray-400 custom-font">—</span>;

const countText = (value) => Number(value ?? 0).toLocaleString('fa-IR');

/** راهنمای فیلد و شمارنده در یک ردیف؛ شمارنده پیش‌فرض antd روی متن راهنما می‌افتد */
const FieldFooter = ({ hint, used, max, unit }) => (
  <div className="flex items-start justify-between gap-3 mt-1 mb-2">
    <span className="min-w-0 flex-1 leading-5">{hint}</span>
    {max != null && (
      <span className={`shrink-0 text-xs leading-5 whitespace-nowrap ${used > max ? 'text-red-500 font-medium' : ''}`}>
        {countText(used)} / {countText(max)} {unit}
      </span>
    )}
  </div>
);

/**
 * قوانین پاسخ خودکار به کامنت‌های اینستاگرام: کامنتی که کلمه را داشته باشد، بسته به قانون پاسخ زیر کامنت و/یا دایرکت می‌گیرد.
 */
const AutoRepliesCard = () => {
  const [rules, setRules] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState({ open: false, editing: null });
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [form] = Form.useForm();
  const replyValue = Form.useWatch('reply_message', form);
  const directValue = Form.useWatch('direct_message', form);
  const targetValue = Form.useWatch('target', form);
  const channelValue = Form.useWatch('channel_id', form);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await liveChatApi.getAutoReplies();
      setRules(res?.data?.rules ?? []);
      setAccounts(res?.data?.accounts ?? []);
    } catch (error) {
      message.error(error?.message || 'خطا در دریافت پاسخ‌های خودکار');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const showWarnings = (res) => (res?.data?.warnings ?? []).forEach((text) => message.warning(text, 6));

  const openModal = (rule = null) => {
    setModal({ open: true, editing: rule });
    form.resetFields();
    form.setFieldsValue(
      rule
        ? {
            keyword: rule.keyword,
            reply_message: rule.reply_message,
            direct_message: rule.direct_message,
            target: rule.external_post_id ? 'post' : 'all',
            channel_id: rule.channel_id ?? accounts[0]?.id,
            external_post_id: rule.external_post_id,
          }
        : { target: 'all', channel_id: accounts[0]?.id },
    );
  };

  const submit = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      // فیلد خالی = حذف آن پیام
      const onePost = values.target === 'post';
      const payload = {
        keyword: values.keyword,
        reply_message: values.reply_message?.trim() || null,
        direct_message: values.direct_message?.trim() || null,
        channel_id: onePost ? values.channel_id : null,
        external_post_id: onePost ? values.external_post_id : null,
      };
      const res = modal.editing
        ? await liveChatApi.updateAutoReply(modal.editing.id, payload)
        : await liveChatApi.createAutoReply(payload);
      message.success('پاسخ خودکار ذخیره شد');
      showWarnings(res);
      setModal({ open: false, editing: null });
      load();
    } catch (error) {
      if (error?.errorFields) return;
      if (error instanceof ValidationError) show422Error(error, form);
      else message.error(error?.message || 'خطا در ذخیره پاسخ خودکار');
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (rule, isActive) => {
    setBusyId(rule.id);
    try {
      const res = await liveChatApi.updateAutoReply(rule.id, { is_active: isActive });
      showWarnings(res);
      setRules((prev) => prev.map((r) => (r.id === rule.id ? { ...r, is_active: isActive } : r)));
    } catch (error) {
      message.error(error?.message || 'خطا در بروزرسانی');
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (rule) => {
    setBusyId(rule.id);
    try {
      const res = await liveChatApi.removeAutoReply(rule.id);
      showWarnings(res);
      setRules((prev) => prev.filter((r) => r.id !== rule.id));
      message.success('پاسخ خودکار حذف شد');
    } catch (error) {
      message.error(error?.message || 'خطا در حذف');
    } finally {
      setBusyId(null);
    }
  };

  const columns = [
    { title: 'کلمه', dataIndex: 'keyword', key: 'keyword', width: 130, render: (v) => <Tag color="magenta" className="custom-font whitespace-normal break-words max-w-full" style={{ marginInlineEnd: 0 }}>{v}</Tag> },
    { title: 'پست', key: 'post', width: 180, render: (_, row) => <PostCell rule={row} accounts={accounts} /> },
    { title: 'پاسخ زیر کامنت', dataIndex: 'reply_message', key: 'reply_message', render: (v) => <MessageCell value={v} /> },
    { title: 'پیام دایرکت', dataIndex: 'direct_message', key: 'direct_message', render: (v) => <MessageCell value={v} /> },
    {
      title: 'ارسال‌شده',
      key: 'sent',
      width: 120,
      render: (_, row) => {
        const failed = Number(row.failed_count ?? 0) + Number(row.direct_failed_count ?? 0);
        return (
          <div className="flex flex-col gap-1 items-start custom-font text-xs">
            {row.reply_message && <span>کامنت: {countText(row.sent_count)}</span>}
            {row.direct_message && <span>دایرکت: {countText(row.direct_sent_count)}</span>}
            {failed > 0 && (
              <Tooltip title={row.last_error}>
                <Tag color="red" icon={<WarningOutlined />} className="custom-font">{countText(failed)} ناموفق</Tag>
              </Tooltip>
            )}
          </div>
        );
      },
    },
    {
      title: 'فعال',
      key: 'is_active',
      width: 64,
      render: (_, row) => <Switch size="small" checked={row.is_active} loading={busyId === row.id} onChange={(v) => toggle(row, v)} />,
    },
    {
      title: '',
      key: 'actions',
      width: 96,
      fixed: 'end',
      render: (_, row) => (
        <Space>
          <Tooltip title="ویرایش">
            <Button size="small" icon={<EditOutlined />} onClick={() => openModal(row)} aria-label="ویرایش" />
          </Tooltip>
          <Popconfirm title="این پاسخ خودکار حذف شود؟" okText="بله" cancelText="خیر" onConfirm={() => remove(row)}>
            <Button size="small" danger icon={<DeleteOutlined />} aria-label="حذف" />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const pollingAccounts = accounts.filter((a) => a.is_active && !a.realtime);
  const pollErrors = accounts.filter((a) => a.poll_error);

  return (
    <div className="livechat-panel p-4">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div className="min-w-0 flex-1 basis-72">
          <div className="font-bold text-gray-800 dark:text-gray-100 custom-font flex items-center gap-2">
            <InstagramOutlined style={{ color: '#e1306c' }} /> کامنت‌های اینستاگرام
          </div>
          <div className="text-xs text-gray-500 custom-font mt-1 leading-5">
            اگر کامنتی شامل کلمه باشد، پاسخ زیر همان کامنت و/یا پیام دایرکت به نویسنده آن ارسال می‌شود. هر کلمه می‌تواند برای همه پست‌ها یا فقط یک پست باشد و پاسخ مخصوص یک پست بر پاسخ همه پست‌ها مقدم است. کامنت‌های قبل از ثبت کلمه پاسخ نمی‌گیرند.
          </div>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => openModal()} className={PANEL_PRIMARY_BTN_CLASS} style={{ fontFamily: 'CustomFont' }}>
          افزودن پاسخ خودکار
        </Button>
      </div>

      {!loading && accounts.length === 0 && (
        <Alert type="warning" showIcon className="custom-font mb-3" title="هنوز حساب اینستاگرامی متصل نیست؛ ابتدا در «تنظیمات و منابع › اتصال منابع» یک حساب متصل کنید." />
      )}
      {rules.length > 0 && pollingAccounts.length > 0 && (
        <Alert
          type="info"
          showIcon
          className="custom-font mb-3"
          title={`حساب ${pollingAccounts.map((a) => a.name).join('، ')} وب‌هوک کامنت ندارد و کامنت‌ها با تأخیر خوانده می‌شوند (از طریق Zernio تا حدود ۱۰ دقیقه). برای پاسخ لحظه‌ای، LIVECHAT_PUBLIC_URL باید آدرس HTTPS عمومی سرور باشد.`}
        />
      )}
      {pollErrors.map((a) => (
        <Alert key={a.id} type="error" showIcon className="custom-font mb-3" title={`${a.name}: ${a.poll_error}`} />
      ))}

      <Spin spinning={loading}>
        {rules.length === 0 && !loading ? (
          <Empty className="py-6" description={<span className="custom-font">هنوز پاسخ خودکاری تعریف نشده است (مثلاً برای کلمه «قیمت»)</span>} />
        ) : (
          <Table rowKey="id" size="small" columns={columns} dataSource={rules} pagination={false} tableLayout="fixed" scroll={{ x: 920 }} />
        )}
      </Spin>

      <Modal
        title={modal.editing ? 'ویرایش پاسخ خودکار' : 'افزودن پاسخ خودکار'}
        open={modal.open}
        onCancel={() => setModal({ open: false, editing: null })}
        onOk={submit}
        confirmLoading={saving}
        destroyOnHidden
        okText="ذخیره"
        cancelText="انصراف"
        okButtonProps={{ className: PANEL_PRIMARY_BTN_CLASS }}
        width={640}
      >
        <Form form={form} layout="vertical" className="custom-font">
          <Form.Item
            name="keyword"
            label="کلمه"
            rules={[{ required: true, whitespace: true, message: 'کلمه را وارد کنید' }]}
            extra={<FieldFooter hint="بزرگی و کوچکی حروف و تفاوت «ي/ی» و «ك/ک» مهم نیست. «قیمت» در «قیمتش چنده» هم پیدا می‌شود." />}
          >
            <Input maxLength={100} placeholder="مثلاً: قیمت" />
          </Form.Item>
          <Form.Item name="target" label="این پاسخ برای کدام پست‌ها باشد؟">
            <Segmented
              block
              options={TARGET_OPTIONS.map((o) => ({ ...o, disabled: o.value === 'post' && accounts.length === 0 }))}
            />
          </Form.Item>
          {targetValue === 'post' && (
            <>
              {accounts.length > 1 && (
                <Form.Item name="channel_id" label="حساب اینستاگرام" rules={[{ required: true, message: 'حساب را انتخاب کنید' }]}>
                  <Select
                    options={accounts.map((a) => ({ value: a.id, label: a.username ? `${a.name} (@${a.username})` : a.name }))}
                    onChange={() => form.setFieldsValue({ external_post_id: undefined })}
                  />
                </Form.Item>
              )}
              {accounts.length <= 1 && (
                <Form.Item name="channel_id" hidden>
                  <Input />
                </Form.Item>
              )}
              <Form.Item
                name="external_post_id"
                label="پست"
                rules={[{ required: true, message: 'یک پست انتخاب کنید' }]}
                extra={<FieldFooter hint="۲۵ پست اخیر حساب نمایش داده می‌شود." />}
              >
                <InstagramPostPicker
                  channelId={channelValue}
                  currentPost={
                    modal.editing?.external_post_id && modal.editing.channel_id === channelValue
                      ? { id: modal.editing.external_post_id, caption: modal.editing.post_caption, permalink: modal.editing.post_permalink }
                      : null
                  }
                />
              </Form.Item>
            </>
          )}
          <Form.Item
            name="reply_message"
            label="پاسخ زیر کامنت (اختیاری)"
            dependencies={['direct_message']}
            extra={<FieldFooter hint="به‌صورت عمومی زیر کامنت نمایش داده می‌شود." used={(replyValue || '').length} max={1000} unit="حرف" />}
            rules={[
              ({ getFieldValue }) => ({
                validator: (_, value) =>
                  value?.trim() || getFieldValue('direct_message')?.trim()
                    ? Promise.resolve()
                    : Promise.reject(new Error('حداقل یکی از دو پیام را وارد کنید')),
              }),
            ]}
          >
            <Input.TextArea autoSize={{ minRows: 2, maxRows: 5 }} maxLength={1000} placeholder="مثلاً: جزئیات را در دایرکت برایتان فرستادیم 🌱" />
          </Form.Item>
          <Form.Item
            name="direct_message"
            label="پیام دایرکت (اختیاری)"
            dependencies={['reply_message']}
            extra={
              <FieldFooter
                hint="به نویسنده کامنت فرستاده می‌شود. اینستاگرام برای هر کامنت فقط یک دایرکت اجازه می‌دهد؛ اگر کامنت‌گذار پیج را دنبال نکند، پیام در پوشه Requests او قرار می‌گیرد."
                used={byteLength(directValue)}
                max={MAX_DIRECT_BYTES}
                unit="بایت"
              />
            }
            rules={[
              {
                validator: (_, value) =>
                  byteLength(value?.trim()) <= MAX_DIRECT_BYTES
                    ? Promise.resolve()
                    : Promise.reject(new Error('متن طولانی است؛ اینستاگرام حداکثر ۱۰۰۰ بایت می‌پذیرد (حدود ۵۰۰ حرف فارسی)')),
              },
            ]}
          >
            <Input.TextArea autoSize={{ minRows: 3, maxRows: 6 }} placeholder="مثلاً: سلام! قیمت و شرایط خرید: ..." />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default AutoRepliesCard;
